import React, {useState, useCallback, useEffect, useRef, FC} from 'react'
import * as XLSX from 'xlsx'
import {warn} from 'app/utils/customToasd'
import {validateExcel} from '../reacContractExcel/validate-contract.service'
import styles from './ExcelToJsonConverter.module.css'
import {AlertModalProps} from 'app/utils/modal/AlertModal'
import {ModalForm} from 'app/utils/modal/ModalForm'

const ExcelToJsonConverter: FC = () => {
  const [file, setFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [logs, setLogs] = useState<{type: string; message: string}[]>([])
  const logsContainerRef = useRef<HTMLDivElement>(null)
  const [alertModal, setAlertModal] = useState<AlertModalProps>()

  useEffect(() => {
    const originalLog = console.log
    const originalError = console.error

    console.log = (...args: any[]) => {
      setLogs((prevLogs) => [...prevLogs, {type: 'success', message: args.join(' ')}])
      originalLog(...args)
    }

    console.error = (...args: any[]) => {
      setLogs((prevLogs) => [...prevLogs, {type: 'error', message: args.join(' ')}])
      originalError(...args)
    }

    return () => {
      console.log = originalLog
      console.error = originalError
    }
  }, [])

  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight
    }
  }, [logs])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setFile(file)
      setLogs([])
      setIsLoading(true)

      const fileName = file.name
      const fileExtension = fileName.split('.').pop()?.toLowerCase()
      if (fileExtension === 'xls' || fileExtension === 'xlsx') {
        const reader = new FileReader()
        reader.onload = (event) => {
          const data = event.target?.result
          if (data) {
            const workbook = XLSX.read(data, {type: 'binary'})
            const sheetNames = workbook.SheetNames
            const jsonData: {[sheetName: string]: any[]} = {}

            sheetNames.forEach((sheetName) => {
              const sheet = workbook.Sheets[sheetName]
              const json = XLSX.utils.sheet_to_json(sheet)
              jsonData[sheetName] = json
            })

            validateExcel(jsonData, setAlertModal)
            setIsLoading(false)
            e.target.value = ''
          }
        }
        reader.readAsBinaryString(file)
      } else {
        warn('Por favor, seleccione un archivo de Excel válido (xls o xlsx)')
        setIsLoading(false)
        e.target.value = ''
      }
    }
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Carga Masiva de Contratos</h2>
        <label htmlFor='file-input' className={styles.fileLabel}>
          Seleccionar Archivo
        </label>
        <input
          type='file'
          id='file-input'
          onChange={handleFileChange}
          className={styles.fileInput}
        />
        {file && <p className={styles.fileName}>Archivo seleccionado: {file.name}</p>}
        {isLoading && <div className={styles.loadingMessage}>Cargando...</div>}
      </div>
      <div className={styles.logsContainer} ref={logsContainerRef}>
        <div className={styles.logsHeader}>
          <h3>Logs de Procesos</h3>
        </div>
        {logs.length === 0 ? (
          <div className={styles.emptyLogs}>No hay logs para mostrar.</div>
        ) : (
          <ul style={{listStyle: 'none', padding: 0}}>
            {logs.map((log, index) => (
              <li
                key={index}
                className={`${styles.logItem} ${
                  log.type === 'success'
                    ? styles.success
                    : log.type === 'error'
                    ? styles.error
                    : styles.warning
                }`}
              >
                {log.message}
              </li>
            ))}
          </ul>
        )}
      </div>
      {alertModal?.message !== undefined && (
        <ModalForm
          validationSchema={alertModal?.validationSchema}
          initialValues={alertModal.initialValues}
          innerHTML={alertModal.innerHTML}
          modalFullscreen={alertModal.modalFullscreen}
          message={alertModal?.message}
          show={alertModal?.message !== undefined}
          closeName={alertModal?.closeName}
          maxLength={alertModal?.maxLength}
          title={alertModal?.title}
          saveName={alertModal?.saveName}
          showSave={alertModal.showSave}
          rows={alertModal.rows}
          hideGrid={alertModal.hideGrid}
          showClose={alertModal?.showClose}
          close={alertModal?.close}
          save={alertModal.save}
          showSecondAction={alertModal.showSecondAction}
          secondActionName={alertModal.secondActionName}
          secondAction={alertModal.secondAction}
          showThirdAction={alertModal.showThirdAction}
          thirdActionName={alertModal.thirdActionName}
          thirdAction={alertModal.thirdAction}
          rowSelection={alertModal.rowSelection}
          suppressRowClickSelection={alertModal.suppressRowClickSelection}
          onClick={alertModal.onClick}
          onChange={alertModal.onChange}
          onFirstDataRendered={alertModal.onFirstDataRendered}
        />
      )}
    </div>
  )
}

export default ExcelToJsonConverter
